import React, {forwardRef, MouseEventHandler, ReactNode, useRef} from 'react'
import type {UnnormalizedProps as UnnormalizedStyleProps} from '@yosmanyga/core-style'
import {InteractFn, SpacialFn, styled, TextFn, VisualFn} from '@yosmanyga/web-style'
import {ButtonProps} from '@yosmanyga/primitive-ui-spec'

const Button = forwardRef<HTMLButtonElement, ButtonProps<
    MouseEventHandler<HTMLButtonElement>
> & UnnormalizedStyleProps>(({
    id,
    disabled,
    upload, onUpload,
    tooltip,
    onClick, onMouseDown, onMouseUp,
    className,
    children
}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)

    children = React.Children.map<ReactNode, ReactNode>(children, (child) => {
        if (
            child
            && typeof child !== 'string'
            && typeof child !== 'number'
            && typeof child !== 'boolean'
            && 'children' in child
        ) {
            return child['children']
        }

        return child
    })

    return <button
        id={id}
        ref={ref}
        onClick={!disabled
            ? (upload
                    ? () => {
                        inputRef.current?.click()
                    }
                    : onClick
            )
            : undefined
        }
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        // disabled={disabled} With native 'disabled', the tooltip won't work
        data-tooltip-id="web-primitive-ui-tooltip"
        data-tooltip-content={tooltip}
        className={className}
    >
        {upload && <input
            type="file"
            ref={inputRef}
            onChange={(event) => {
                if (event.target.files === null) {
                    return
                }

                const files: File[] = Array.from(event.target.files); // Convert FileList to Array<File>

                // Needed to detect onChange for same file
                event.target.value = ''

                onUpload && onUpload(files);
            }}
            multiple={upload === 'multiple'}
            style={{display: 'none'}}
        />}
        {children}
    </button>
})

const StyledButton = styled(Button)`
    ${({flow}) => SpacialFn.compileFlow(SpacialFn.normalizeFlow(flow))}
    ${({align}) => SpacialFn.compileAlign(align)}
    ${({margin}) => SpacialFn.compileMargin(SpacialFn.normalizeMargin(margin))}
    ${({padding}) => SpacialFn.compilePadding(SpacialFn.normalizePadding(padding))}
    ${({width}) => SpacialFn.compileWidth(SpacialFn.normalizeWidth(width))}
    ${({height}) => SpacialFn.compileHeight(SpacialFn.normalizeHeight(height))}
    ${({position}) => SpacialFn.compilePosition(SpacialFn.normalizePosition(position))}
    ${({border}) => VisualFn.compileBorder(VisualFn.normalizeBorder(border))}
    ${({background}) => VisualFn.compileBackground(VisualFn.normalizeBackground(background))}
    ${({color}) => VisualFn.compileColor(color)}
    ${({opacity}) => VisualFn.compileOpacity(opacity)}
    ${({cursor}) => InteractFn.compileCursor(cursor)}
    &:hover {
        ${({pseudo}) => VisualFn.compileColor(pseudo?.hover?.color)}
        ${({pseudo}) => VisualFn.compileBackground(VisualFn.normalizeBackground(pseudo?.hover?.background))}
        ${({pseudo}) => VisualFn.compileBorder(VisualFn.normalizeBorder(pseudo?.hover?.border))}
        ${({pseudo}) => TextFn.compileDecoration(pseudo?.hover?.decoration)}
    }

    ${({font}) => TextFn.compileFont(font)}
        // Pass props to children
    > * {
        ${({color}) => VisualFn.compileColor(color)}
        ${({color}) => VisualFn.compileFill(color)}

        ${({font}) => TextFn.compileFont(font)}
    }

    &:hover * {
        ${({pseudo}) => VisualFn.compileColor(pseudo?.hover?.color)}
        //Bad behavior on hover icon
        //${({pseudo}) => VisualFn.compileFill(pseudo?.hover?.color)}
    }
`

export default StyledButton
